import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  LinearProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@mui/icons-material';
import {
  formatExitReason,
  formatSignalState,
  getExitReasonStyle,
  getReversalCatcherLocale,
} from './reversalCatcherFormatters';

function StatCell({ label, value, sub, valueColor = '#0f172a', isLast }) {
  return (
    <Box sx={{
      flex: '1 1 0',
      minWidth: 72,
      px: 1.25,
      py: 0.7,
      borderRight: isLast ? 'none' : '1px solid #eef2f7',
    }}>
      <Typography sx={{
        fontSize: '0.58rem',
        fontWeight: 600,
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        lineHeight: 1.2,
        mb: 0.25,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: '0.88rem',
        fontWeight: 700,
        color: valueColor,
        fontFamily: 'ui-monospace, monospace',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.15, lineHeight: 1.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function PerformanceStatsGrid({ data, t }) {
  const wins = data.closed_signals > 0 ? Math.round(data.win_rate * data.closed_signals) : 0;
  const losses = data.closed_signals - wins;
  const pnlTone = (v) => (v > 0 ? '#0f766e' : v < 0 ? '#b91c1c' : '#0f172a');

  const cells = [
    {
      key: 'signals',
      label: t('reversalCatcher.performance.pillSignals'),
      value: String(data.total_signals),
      sub: `${data.active_signals ?? 0} ${t('reversalCatcher.open')} · ${data.closed_signals ?? 0} ${t('reversalCatcher.closed')}`,
    },
  ];

  if (data.closed_signals > 0) {
    cells.push({
      key: 'wr',
      label: t('reversalCatcher.performance.pillWinRate'),
      value: `${(data.win_rate * 100).toFixed(0)}%`,
      sub: t('reversalCatcher.performance.winLossShort', { wins, losses }),
      valueColor: data.win_rate >= 0.5 ? '#0f766e' : data.win_rate >= 0.35 ? '#0f172a' : '#b91c1c',
    });
    cells.push({
      key: 'ret',
      label: t('reversalCatcher.performance.pillAvgReturn'),
      value: `${data.avg_return >= 0 ? '+' : ''}${(data.avg_return * 100).toFixed(1)}%`,
      valueColor: pnlTone(data.avg_return),
    });
  }

  if (data.avg_days_held != null && data.closed_signals > 0) {
    cells.push({
      key: 'hold',
      label: t('reversalCatcher.performance.pillHolding'),
      value: `${data.avg_days_held.toFixed(0)} ${t('reversalCatcher.days')}`,
    });
  }

  return (
    <Box sx={{
      display: 'flex',
      flexWrap: { xs: 'wrap', md: 'nowrap' },
      bgcolor: '#fff',
      borderTop: '1px solid #eef2f7',
    }}>
      {cells.map((cell, i) => (
        <StatCell key={cell.key} {...cell} isLast={i === cells.length - 1} />
      ))}
    </Box>
  );
}

function CollapsedTradeHint({ data, t }) {
  const openTickers = (data.active_signals_list || []).map((s) => s.ticker);
  const closedN = data.closed_signals_list?.length ?? 0;
  const parts = [];

  if (openTickers.length > 0) {
    parts.push(t('reversalCatcher.performance.collapsedOpen', { tickers: openTickers.join(', ') }));
  }
  if (closedN > 0) {
    parts.push(t('reversalCatcher.performance.collapsedClosed', { count: closedN }));
  }

  if (!parts.length) return null;

  return (
    <Typography sx={{
      px: 1.25,
      py: 0.55,
      fontSize: '0.65rem',
      color: '#64748b',
      lineHeight: 1.45,
      borderTop: '1px solid #f1f5f9',
      bgcolor: '#fafbfc',
    }}>
      {parts.join(' · ')}
    </Typography>
  );
}

const PnL = ({ value, bold = false, size = '0.78rem' }) => {
  const pct = (value * 100).toFixed(2);
  const color = value > 0 ? '#059669' : value < 0 ? '#dc2626' : '#64748b';
  return (
    <Typography sx={{ fontSize: size, fontWeight: bold ? 800 : 600, color, fontFamily: 'monospace' }}>
      {value >= 0 ? '+' : ''}{pct}%
    </Typography>
  );
};

const PerformanceMetrics = ({ data, sx = {} }) => {
  const { t, i18n } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const dateLocale = getReversalCatcherLocale(i18n.language);

  if (!data) return null;

  const hasActive = data.active_signals_list?.length > 0;
  const hasClosed = data.closed_signals_list?.length > 0;
  const hasDetails = hasActive || hasClosed;
  const toggleDetails = () => {
    if (hasDetails) setDetailsOpen((v) => !v);
  };

  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          borderRadius: 1,
          border: '1px solid #e5e7eb',
          bgcolor: '#fff',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={toggleDetails}
          role={hasDetails ? 'button' : undefined}
          tabIndex={hasDetails ? 0 : undefined}
          onKeyDown={(e) => {
            if (hasDetails && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              toggleDetails();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.25,
            py: 0.6,
            cursor: hasDetails ? 'pointer' : 'default',
            userSelect: 'none',
            '&:hover': hasDetails ? { bgcolor: '#fafbfc' } : {},
          }}
        >
          <Typography sx={{
            fontSize: '0.62rem',
            fontWeight: 600,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            {t('reversalCatcher.performance.title')}
          </Typography>
          {hasDetails && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, color: '#64748b' }}>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                {detailsOpen
                  ? t('reversalCatcher.performance.collapse')
                  : t('reversalCatcher.performance.tradeHistory')}
              </Typography>
              <ExpandMore sx={{
                fontSize: 18,
                transform: detailsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }} />
            </Box>
          )}
        </Box>

        <PerformanceStatsGrid data={data} t={t} />

        {!detailsOpen && hasDetails && (
          <CollapsedTradeHint data={data} t={t} />
        )}

        <Collapse in={detailsOpen && hasDetails}>
          <Box sx={{ px: 1.25, py: 1.1, bgcolor: '#fafbfc', borderTop: '1px solid #eef2f7' }}>
            {hasActive && <ActiveSignalsTable data={data} t={t} dateLocale={dateLocale} />}
            {hasClosed && (
              <Box sx={{ mt: hasActive ? 1.5 : 0 }}>
                <ClosedSignalsTable data={data} t={t} dateLocale={dateLocale} />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

function ActiveSignalsTable({ data, t, dateLocale }) {
  const avgPl = (data.active_signals_list.reduce((s, x) => s + (x.pct_change || 0), 0) / data.active_signals_list.length) * 100;
  const avgPlLabel = t('reversalCatcher.performance.avgPlOpen', {
    value: `${avgPl >= 0 ? '+' : ''}${avgPl.toFixed(2)}%`,
  });

  const headers = [
    t('reversalCatcher.ticker'),
    t('reversalCatcher.entryDate'),
    t('reversalCatcher.entryPrice'),
    t('reversalCatcher.currentPrice'),
    t('reversalCatcher.pnl'),
    t('reversalCatcher.table.days'),
    t('reversalCatcher.table.setup'),
    t('reversalCatcher.stop'),
    t('reversalCatcher.target'),
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', mb: 0.6 }}>
        {data.active_signals_list.length} {t('reversalCatcher.activePositions')}
        <Typography component="span" sx={{ fontWeight: 600, color: '#94a3b8', ml: 0.75 }}>
          · {avgPlLabel}
        </Typography>
      </Typography>
      <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1.2fr 0.7fr 1fr 0.8fr 0.8fr',
          p: '6px 10px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}>
          {headers.map((h, i) => (
            <Typography key={i} sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>
              {h}
            </Typography>
          ))}
        </Box>
        {data.active_signals_list.map((s, idx) => {
          const profit = s.pct_change > 0;
          return (
            <Box key={idx} sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1.2fr 0.7fr 1fr 0.8fr 0.8fr',
              p: '6px 10px',
              borderBottom: '1px solid #f8fafc',
              bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
            }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>
                {new Date(s.signal_date).toLocaleDateString(dateLocale)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#475569', fontFamily: 'monospace' }}>
                {s.entry_price.toLocaleString(dateLocale, { maximumFractionDigits: 0 })}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, fontFamily: 'monospace' }}>
                {s.current_price.toLocaleString(dateLocale, { maximumFractionDigits: 0 })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PnL value={s.pct_change} bold />
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Math.abs(s.pct_change * 100) * 5, 100)}
                  sx={{
                    flex: 1, height: 3, borderRadius: 2, maxWidth: 32,
                    bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': { bgcolor: profit ? '#22c55e' : '#ef4444', borderRadius: 2 },
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace', textAlign: 'center' }}>{s.days_held}</Typography>
              <Chip label={formatSignalState(t, s.state)} size="small" sx={{ height: 17, fontSize: '0.55rem', fontWeight: 700, width: 'fit-content' }} />
              <Typography sx={{ fontSize: '0.66rem', color: '#dc2626', fontFamily: 'monospace', fontWeight: 600 }}>
                {s.stop ? s.stop.toLocaleString(dateLocale, { maximumFractionDigits: 0 }) : '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: '#059669', fontFamily: 'monospace', fontWeight: 600 }}>
                {s.target1 ? s.target1.toLocaleString(dateLocale, { maximumFractionDigits: 0 }) : '—'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ClosedSignalsTable({ data, t, dateLocale }) {
  const wins = data.closed_signals_list.filter((s) => s.pct_change > 0).length;
  const losses = data.closed_signals_list.length - wins;

  const headers = [
    t('reversalCatcher.ticker'),
    t('reversalCatcher.buyDate'),
    t('reversalCatcher.entryPrice'),
    t('reversalCatcher.exitPrice'),
    t('reversalCatcher.pnl'),
    t('reversalCatcher.table.days'),
    t('reversalCatcher.table.setup'),
    t('reversalCatcher.table.exitReason'),
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', mb: 0.6 }}>
        {data.closed_signals_list.length} {t('reversalCatcher.closedSignals')}
        <Typography component="span" sx={{ fontWeight: 600, color: '#94a3b8', ml: 0.75 }}>
          · {t('reversalCatcher.performance.winLossShort', { wins, losses })}
        </Typography>
      </Typography>
      <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1.2fr 0.7fr 1fr 1.2fr',
          p: '6px 10px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}>
          {headers.map((h, i) => (
            <Typography key={i} sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>
              {h}
            </Typography>
          ))}
        </Box>
        {data.closed_signals_list.map((s, idx) => {
          const style = getExitReasonStyle(s.exit_reason);
          const reasonLabel = formatExitReason(t, s.exit_reason);
          return (
            <Box key={idx} sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr 1.2fr 0.7fr 1fr 1.2fr',
              p: '6px 10px',
              borderBottom: '1px solid #f8fafc',
              bgcolor: idx % 2 === 1 ? '#fafbfc' : '#fff',
            }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{s.ticker}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'monospace' }}>
                {new Date(s.signal_date).toLocaleDateString(dateLocale)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>{s.entry_price.toLocaleString(dateLocale)}</Typography>
              <Typography sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>{s.exit_price.toLocaleString(dateLocale)}</Typography>
              <PnL value={s.pct_change} bold />
              <Typography sx={{ fontSize: '0.68rem', textAlign: 'center', fontFamily: 'monospace' }}>{s.days_held}</Typography>
              <Chip label={formatSignalState(t, s.state)} size="small" sx={{ height: 17, fontSize: '0.55rem', width: 'fit-content' }} />
              <Chip label={`${style.icon} ${reasonLabel}`} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: style.bg, color: style.color, width: 'fit-content' }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default PerformanceMetrics;
